import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'app/core/auth/auth.service';

@Component({
    selector: 'app-change-password-dialog',
    templateUrl: './change-password-dialog.component.html',
    styleUrls: ['./change-password-dialog.component.scss'],
})
export class ChangePasswordDialogComponent {
    changePasswordForm: FormGroup;
    loading = false;
    errorMessage = '';
    successMessage = '';
    showOld = false;
    showNew = false;
    showConfirm = false;

    strengthLabel = 'Too weak';
    strengthColor = '#c62828';
    requirements = {
        length: false,
        upper: false,
        lower: false,
        number: false,
        special: false,
    };

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        private dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { username: string }
    ) {
        this.changePasswordForm = this.fb.group(
            {
                oldPassword: ['', Validators.required],
                newPassword: ['', Validators.required],
                confirmPassword: ['', Validators.required],
            },
            {
                validators: [
                    this.passwordsMatchValidator,
                    this.passwordsCannotMatchValidator,
                ],
            }
        );
    }

    /** Validate that confirm password matches */
    private passwordsMatchValidator(form: FormGroup): null | object {
        const newPass = form.get('newPassword')?.value;
        const confirm = form.get('confirmPassword')?.value;
        return newPass === confirm ? null : { passwordsMismatch: true };
    }

    /** Validate that new password != old password */
    private passwordsCannotMatchValidator(form: FormGroup): null | object {
        const oldPass = form.get('oldPassword')?.value;
        const newPass = form.get('newPassword')?.value;
        return oldPass && newPass && oldPass === newPass
            ? { samePassword: true }
            : null;
    }

    /** Live password strength and requirements check */
    onPasswordInput(): void {
        const password =
            this.changePasswordForm.get('newPassword')?.value || '';

        this.requirements = {
            length: password.length >= 14,
            upper: /[A-Z]/.test(password),
            lower: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        };

        const score =
            Object.values(this.requirements).filter(Boolean).length +
            (password.length >= 18 ? 1 : 0);

        if (score <= 2) {
            this.strengthLabel = 'Weak';
            this.strengthColor = '#c62828';
        } else if (score === 3 || score === 4) {
            this.strengthLabel = 'Medium';
            this.strengthColor = '#f9a825';
        } else {
            this.strengthLabel = 'Strong';
            this.strengthColor = '#2e7d32';
        }
    }

    /** Submit password change */
    submit(): void {
        if (this.changePasswordForm.invalid) {
            if (this.changePasswordForm.hasError('samePassword')) {
                this.errorMessage =
                    'New password cannot be the same as the old password.';
            } else if (this.changePasswordForm.hasError('passwordsMismatch')) {
                this.errorMessage = 'New and confirm passwords do not match.';
            } else {
                this.errorMessage = 'Please fix the validation errors.';
            }
            return;
        }

        const { oldPassword, newPassword } = this.changePasswordForm.value;
        const meetsAll =
            Object.values(this.requirements).every(Boolean) &&
            newPassword.length >= 14;

        if (!meetsAll) {
            this.errorMessage =
                'Password does not meet the required security standards.';
            return;
        }

        this.loading = true;

        this.authService
            .changePassword({
                username: this.data.username,
                oldPassword,
                newPassword,
            })
            .subscribe({
                next: () => {
                    this.loading = false;
                    this.successMessage = 'Password changed successfully!';
                    setTimeout(() => {
                        this.dialogRef.close({
                            changed: true,
                        });
                    }, 1000);
                },
                error: (err) => {
                    this.loading = false;
                    this.errorMessage =
                        err?.error?.msg ||
                        err?.error?.workload ||
                        'Failed to change password. Please try again.';
                },
            });
    }

    cancel(): void {
        this.dialogRef.close({ changed: false, canceled: true });
    }

    getStrengthClass(): string {
        switch (this.strengthLabel?.toLowerCase()) {
            case 'weak':
                return 'weak';
            case 'medium':
                return 'medium';
            case 'strong':
                return 'strong';
            default:
                return '';
        }
    }
}
